/**
 * PO Detail Page
 *
 * Displays a single purchase order with its original document.
 * Features:
 * - Split view: Original document (left) + PO data (right)
 * - Edit mode toggle
 * - Status management (Approve, Archive)
 * - Navigation back to list
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePageContext } from '../context/PageContext';
import DocumentViewer from '../components/documents/DocumentViewer';
import POForm from '../components/purchase-orders/POForm';
import QueueDetailDrawer from '../components/documents/QueueDetailDrawer';
import Button from '../components/common/Button';
import StatusBadge from '../components/common/StatusBadge';
import SparklesIcon from '../components/common/SparklesIcon';
import { useAuth } from '../context/AuthContext';
import * as purchaseOrderApi from '../api/purchaseOrderApi';
import { getQueueEntryByPO } from '../api/queueApi';
import * as fileApi from '../api/fileApi';
import { formatCurrency } from '../utils/formatCurrency';
import { formatDateFull, formatDate } from '../utils/formatDate';
import { getCountryName } from '../constants/countries';

/**
 * Format a structured address for display
 * @param {Object} address - Structured address object
 * @returns {string} Formatted address string
 */
function formatAddress(address) {
  if (!address) return null;

  // Handle legacy string addresses
  if (typeof address === 'string') return address;

  const parts = [];

  // Build address lines
  const addressLines = [
    address.addressLine1,
    address.addressLine2,
    address.addressLine3
  ].filter(Boolean);
  if (addressLines.length > 0) parts.push(addressLines.join('\n'));

  // City, State PostalCode on one line
  const cityLine = [
    address.city,
    address.state,
    address.postalCode
  ].filter(Boolean).join(', ');
  if (cityLine) parts.push(cityLine);

  // Country name
  if (address.country) {
    parts.push(getCountryName(address.country));
  }

  return parts.length > 0 ? parts.join('\n') : null;
}

/**
 * PODetail Component
 */
function PODetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setPageContext, clearPageContext } = usePageContext();

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // State
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Document signed URL state
  const [documentSignedUrl, setDocumentSignedUrl] = useState(null);
  const [documentMimeType, setDocumentMimeType] = useState(null);

  // Queue detail drawer state
  const [queueEntry, setQueueEntry] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  /**
   * Fetch purchase order data
   */
  useEffect(() => {
    const fetchPurchaseOrder = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await purchaseOrderApi.getPurchaseOrder(id);
        setPurchaseOrder(response.data);
      } catch (err) {
        setError(err.message || 'Failed to load purchase order');
      } finally {
        setLoading(false);
      }
    };

    fetchPurchaseOrder();
  }, [id]);

  // Fetch associated queue entry
  useEffect(() => {
    if (!purchaseOrder) return;
    getQueueEntryByPO(purchaseOrder._id)
      .then(response => {
        setQueueEntry(response.data);
        setIsDrawerOpen(true);
      })
      .catch(() => setQueueEntry(null));
  }, [purchaseOrder?._id]);

  // Publish page context so the ChatWidget can inform the agent about this PO.
  useEffect(() => {
    if (!purchaseOrder) return;

    const parties = (purchaseOrder.parties || []).map(p => ({
      type: p.type,
      name: p.name,
    }));

    setPageContext({
      page: 'Purchase Order Detail',
      poNumber: purchaseOrder.header?.poNumber || '(unknown)',
      poId: purchaseOrder._id,
      status: purchaseOrder.status,
      parties,
      itemCount: purchaseOrder.items?.length || 0,
      grandTotal: purchaseOrder.totals?.grandTotal,
      currency: purchaseOrder.header?.currency || 'USD',
    });

    return () => clearPageContext();
  }, [purchaseOrder, setPageContext, clearPageContext]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Build proxy URL for document when PO is loaded (avoids CORS issues)
  useEffect(() => {
    if (!purchaseOrder || !purchaseOrder.documentObjectKey) return;

    // Use proxy URL through backend to avoid CORS issues
    setDocumentSignedUrl(fileApi.buildProxyUrl(purchaseOrder.documentObjectKey));
    // Determine mime type from file extension
    const ext = purchaseOrder.documentObjectKey.split('.').pop().toLowerCase();
    const mimeMap = {
      pdf: 'application/pdf',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      webp: 'image/webp',
      tiff: 'image/tiff',
      tif: 'image/tiff',
      csv: 'text/csv',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xls: 'application/vnd.ms-excel',
      edi: 'text/plain',
      txt: 'text/plain',
    };
    setDocumentMimeType(mimeMap[ext] || 'application/octet-stream');
  }, [purchaseOrder]);

  /**
   * Handle form submission (update PO)
   */
  const handleSubmit = async (formData) => {
    setIsSaving(true);
    setError(null);

    try {
      const response = await purchaseOrderApi.updatePurchaseOrder(id, formData);
      setPurchaseOrder(response.data);
      setIsEditing(false);
      setSuccessMessage('Purchase order updated successfully!');
    } catch (err) {
      setError(err.message || 'Failed to update purchase order');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle status change
   */
  const handleStatusChange = async (newStatus) => {
    setIsUpdatingStatus(true);
    setError(null);

    try {
      const response = await purchaseOrderApi.updateStatus(id, newStatus);
      setPurchaseOrder(response.data);
      setSuccessMessage(`Purchase order ${newStatus.toLowerCase()} successfully!`);
    } catch (err) {
      setError(err.message || 'Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  /**
   * Handle delete
   */
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this purchase order? This action cannot be undone.')) {
      return;
    }

    try {
      await purchaseOrderApi.deletePurchaseOrder(id);
      navigate('/purchase-orders', {
        state: { message: 'Purchase order deleted successfully!' }
      });
    } catch (err) {
      setError(err.message || 'Failed to delete purchase order');
    }
  };

  // Document visibility state
  const [isDocumentVisible, setIsDocumentVisible] = useState(false);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading purchase order...</p>
        </div>
      </div>
    );
  }

  // Error state (fatal - couldn't load PO)
  if (error && !purchaseOrder) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center max-w-md">
          <svg
            className="mx-auto h-16 w-16 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="mt-4 text-xl font-medium text-gray-900">
            Failed to Load Purchase Order
          </h2>
          <p className="mt-2 text-gray-600">{error}</p>
          <div className="mt-4 space-x-4">
            <button
              onClick={() => window.location.reload()}
              className="text-blue-600 hover:underline"
            >
              Try Again
            </button>
            <span className="text-gray-400">|</span>
            <Link to="/purchase-orders" className="text-blue-600 hover:underline">
              Back to List
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const hasDocument = !!documentSignedUrl;
  const showDocument = hasDocument && isDocumentVisible;

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Page Header */}
      <div className="flex-shrink-0 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">
                  {purchaseOrder?.header?.poNumber || 'Purchase Order'}
                </h1>
                <StatusBadge status={purchaseOrder?.status} variant="po" />
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Created {formatDateFull(purchaseOrder?.createdAt)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Document Toggle */}
            {hasDocument && (
              <Button
                variant="secondary"
                onClick={() => setIsDocumentVisible(!isDocumentVisible)}
                title={isDocumentVisible ? "Hide Original Document" : "Show Original Document"}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isDocumentVisible ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  )}
                </svg>
              </Button>
            )}

            {/* Queue details toggle */}
            {queueEntry && (
              <button
                onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                className={`p-2 rounded-md border transition-colors ${isDrawerOpen
                  ? 'bg-blue-50 border-blue-200 text-blue-600'
                  : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
                title={isDrawerOpen ? 'Hide AI extraction details' : 'AI extraction details'}
              >
                <SparklesIcon className="w-5 h-5" />
              </button>
            )}

            <div className="h-6 w-px bg-gray-300 mx-1"></div>

            {/* Edit toggle */}
            {!isEditing && (
              <Button
                variant="secondary"
                onClick={() => setIsEditing(true)}
                title="Edit Purchase Order"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </Button>
            )}

            {isEditing && (
              <Button
                variant="secondary"
                onClick={() => setIsEditing(false)}
              >
                Cancel Edit
              </Button>
            )}

            {/* More actions menu */}
            <div className="relative">
              <Button
                variant="secondary"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                title="More actions"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="12" cy="19" r="2" />
                </svg>
              </Button>
              {isMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setIsMenuOpen(false)} />
                  <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                    {purchaseOrder?.status === 'ACTIVE' && (
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        disabled={isUpdatingStatus}
                        onClick={() => { setIsMenuOpen(false); handleStatusChange('ARCHIVED'); }}
                      >
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        Archive
                      </button>
                    )}
                    {purchaseOrder?.status === 'ARCHIVED' && (
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                        disabled={isUpdatingStatus}
                        onClick={() => { setIsMenuOpen(false); handleStatusChange('ACTIVE'); }}
                      >
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Restore
                      </button>
                    )}
                    {isAdmin && (
                      <>
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          onClick={() => { setIsMenuOpen(false); handleDelete(); }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
          </div>
        </div>
      </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="flex-shrink-0 mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="ml-2 text-sm text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Message (non-fatal) */}
      {error && purchaseOrder && (
        <div className="flex-shrink-0 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="ml-2 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Split Pane Container */}
      <div className="flex-1 flex mt-4 min-h-0 gap-2">
        {/* Left Pane - Document Viewer */}
        <div className={`${showDocument ? 'w-[40%] shrink-0' : 'w-0 hidden'} border border-gray-200 rounded-lg overflow-hidden transition-all duration-300`}>
          <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
            <h2 className="text-sm font-medium text-gray-700">
              Original Document
            </h2>
          </div>
          <div className="h-[calc(100%-2.5rem)]">
            <DocumentViewer
              url={documentSignedUrl}
              mimeType={documentMimeType}
              objectKey={purchaseOrder.documentObjectKey}
              filename={queueEntry?.files?.[0]?.originalName || purchaseOrder.documentObjectKey.split('/').pop()}
              detectedFileType={queueEntry?.files?.[0]?.detectedFileType}
            />
          </div>
        </div>

        {/* Right Pane - PO Data */}
        <div className="flex-1 min-w-0 border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-100 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-sm font-medium text-gray-700">
              Purchase Order Data
            </h2>
            {isEditing && (
              <span className="text-xs text-blue-600 font-medium">Editing</span>
            )}
          </div>
          <div className="h-[calc(100%-2.5rem)] overflow-auto p-4">
            {isEditing ? (
              <POForm
                initialData={purchaseOrder}
                onSubmit={handleSubmit}
                isSubmitting={isSaving}
                submitLabel="Save Changes"
              />
            ) : (
              <POReadOnlyView purchaseOrder={purchaseOrder} />
            )}
          </div>
        </div>
      </div>

      {/* Overlay drawer for queue details + extraction pipeline */}
      <QueueDetailDrawer
        open={isDrawerOpen && !!queueEntry}
        queueEntry={queueEntry}
        evaluation={queueEntry?.evaluation}
        confidence={queueEntry?.confidence}
        onClose={() => setIsDrawerOpen(false)}
      />
    </div>
  );
}

/**
 * Read-only view of PO data
 */
function POReadOnlyView({ purchaseOrder }) {
  const calculateTotal = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => {
      return sum + ((item.quantity || 0) * (item.unitPrice || 0));
    }, 0);
  };

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Header Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">PO Number</label>
            <p className="mt-1 text-sm text-gray-900">{purchaseOrder?.header?.poNumber || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Date</label>
            <p className="mt-1 text-sm text-gray-900">
              {purchaseOrder?.header?.date
                ? new Date(purchaseOrder.header.date).toLocaleDateString()
                : '-'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Currency</label>
            <p className="mt-1 text-sm text-gray-900">{purchaseOrder?.header?.currency || 'USD'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Payment Terms</label>
            <p className="mt-1 text-sm text-gray-900">{purchaseOrder?.header?.paymentTerms || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Incoterms</label>
            <p className="mt-1 text-sm text-gray-900">{purchaseOrder?.header?.incoterms || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Incoterms Location</label>
            <p className="mt-1 text-sm text-gray-900">{purchaseOrder?.header?.incotermsLocation || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Required By</label>
            <p className="mt-1 text-sm text-gray-900">
              {purchaseOrder?.header?.requiredBy
                ? new Date(purchaseOrder.header.requiredBy).toLocaleDateString()
                : '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Parties Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Parties</h3>
        {purchaseOrder?.parties?.length > 0 ? (
          <div className="flex flex-wrap gap-4">
            {purchaseOrder.parties.map((party, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 flex-1 min-w-[300px] border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                    {party.type}
                  </span>
                  <span className="font-medium text-gray-900">{party.name}</span>
                </div>
                {formatAddress(party.address) && (
                  <p className="text-sm text-gray-600 whitespace-pre-line">{formatAddress(party.address)}</p>
                )}
                {party.id_code && (
                  <p className="text-sm text-gray-500 mt-2">ID: {party.id_code}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No parties defined</p>
        )}
      </div>

      {/* Items Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Line Items</h3>
        {purchaseOrder?.items?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Line</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {purchaseOrder.items.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 text-sm text-gray-900">{item.lineId || index + 1}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{item.sku || '-'}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{item.description || '-'}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 text-right">
                      {item.quantity} {item.uom || ''}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 text-right">
                      {formatCurrency(item.unitPrice, purchaseOrder?.header?.currency)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 text-right font-medium">
                      {formatCurrency((item.quantity || 0) * (item.unitPrice || 0), purchaseOrder?.header?.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                {/* Subtotal (computed from line items) */}
                <tr>
                  <td colSpan="5" className="px-4 py-2 text-sm text-gray-600 text-right">
                    Subtotal:
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 text-right">
                    {formatCurrency(calculateTotal(purchaseOrder.items), purchaseOrder?.header?.currency)}
                  </td>
                </tr>
                {/* Tax */}
                {purchaseOrder.totals?.tax != null && purchaseOrder.totals.tax !== 0 && (
                  <tr>
                    <td colSpan="5" className="px-4 py-2 text-sm text-gray-600 text-right">
                      Tax:
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 text-right">
                      {formatCurrency(purchaseOrder.totals.tax, purchaseOrder?.header?.currency)}
                    </td>
                  </tr>
                )}
                {/* Shipping */}
                {purchaseOrder.totals?.shipping != null && purchaseOrder.totals.shipping !== 0 && (
                  <tr>
                    <td colSpan="5" className="px-4 py-2 text-sm text-gray-600 text-right">
                      Shipping:
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 text-right">
                      {formatCurrency(purchaseOrder.totals.shipping, purchaseOrder?.header?.currency)}
                    </td>
                  </tr>
                )}
                {/* Discount */}
                {purchaseOrder.totals?.discount != null && purchaseOrder.totals.discount !== 0 && (
                  <tr>
                    <td colSpan="5" className="px-4 py-2 text-sm text-gray-600 text-right">
                      Discount:
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 text-right">
                      -{formatCurrency(purchaseOrder.totals.discount, purchaseOrder?.header?.currency)}
                    </td>
                  </tr>
                )}
                {/* Grand Total */}
                <tr className="border-t border-gray-300">
                  <td colSpan="5" className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                    Grand Total:
                  </td>
                  <td className="px-4 py-2 text-sm font-bold text-gray-900 text-right">
                    {formatCurrency(
                      purchaseOrder.totals?.grandTotal != null
                        ? purchaseOrder.totals.grandTotal
                        : calculateTotal(purchaseOrder.items),
                      purchaseOrder?.header?.currency
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No line items</p>
        )}
      </div>

      {/* Document Sections (Terms & Conditions, Special Instructions, etc.) */}
      {purchaseOrder?.sections?.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Document Sections</h3>
          <div className="space-y-4">
            {purchaseOrder.sections
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((section, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                      {section.type?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    {section.title && (
                      <span className="font-medium text-gray-900">{section.title}</span>
                    )}
                  </div>
                  {section.items?.length > 0 ? (
                    <ul className="space-y-1 ml-4">
                      {section.items.map((item, itemIdx) => (
                        <li key={itemIdx} className="text-sm text-gray-700">
                          {item.label && (
                            <span className="text-gray-500 mr-2 font-mono">{item.label}</span>
                          )}
                          {item.text}
                        </li>
                      ))}
                    </ul>
                  ) : section.rawText && (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{section.rawText}</p>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Metadata Section */}
      {purchaseOrder?.extractionConfidence && Object.keys(purchaseOrder.extractionConfidence).length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">AI Extraction Confidence</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(purchaseOrder.extractionConfidence).map(([field, value]) => {
                // Handle array values (new per-field format for parties/items)
                if (Array.isArray(value)) {
                  // Compute average across all entries' fields
                  const allScores = value.flatMap(entry =>
                    entry && typeof entry === 'object' ? Object.values(entry).filter(v => typeof v === 'number') : []
                  );
                  if (allScores.length === 0) return null;
                  const avg = allScores.reduce((a, b) => a + b, 0) / allScores.length;
                  return (
                    <div key={field} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{field} (avg)</span>
                      <span className={`text-sm font-medium ${avg >= 0.8 ? 'text-green-600' : 'text-yellow-600'}`}>
                        {Math.round(avg * 100)}%
                      </span>
                    </div>
                  );
                }
                // Scalar value (header fields, sections, totals, overall, or old format)
                if (typeof value !== 'number') return null;
                return (
                  <div key={field} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{field}</span>
                    <span className={`text-sm font-medium ${value >= 0.8 ? 'text-green-600' : 'text-yellow-600'}`}>
                      {Math.round(value * 100)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PODetail;
